import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useParams } from 'react-router-dom';
import { ServicePlanItemPricingPolicy } from 'src/features/servicePlanPricingPolicies/utils/types';
import { useDeleteServicePlanPricingPolicy } from 'src/features/servicePlanPricingPolicies/api/useDeleteServicePlanPricingPolicy';
import { LIST_SERVICE_PLAN_PRICING_POLICIES_RESOURCE } from 'src/features/servicePlanPricingPolicies/api/useListServicePlanPricingPolicies';
import { pricingPoliciesMessages } from 'src/features/servicePlanPricingPolicies/utils/messages';
import { useOnError } from 'src/utils/errors';

interface DeleteServicePlanPricingPolicyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanPricingPolicy: ServicePlanItemPricingPolicy;
    children?: React.ReactNode;
}

function DeleteServicePlanPricingPolicyFormModal<TData>({
    selectedServicePlanPricingPolicy,
    children,
}: DeleteServicePlanPricingPolicyFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { servicePlanItemId = '' } = useParams();

    const onError = useOnError();

    const { mutate: deletePricingPolicy } = useDeleteServicePlanPricingPolicy({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLAN_PRICING_POLICIES_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(pricingPoliciesMessages.deleteServicePlanPricingPolicySuccess),
                description: formatMessage(pricingPoliciesMessages.deleteServicePlanPricingPolicySuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...pricingPoliciesMessages.deleteServicePlanPricingPolicy },
                subTitle: {
                    ...pricingPoliciesMessages.deleteServicePlanPricingPolicySubtitle,
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...pricingPoliciesMessages.confirmDeleteServicePlanPricingPolicy },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: pricingPoliciesMessages.deleteServicePlanPricingPolicy,
                callbackFn: () => {
                    return (
                        deletePricingPolicy({
                            servicePlanItemId,
                            pricingPolicyId: selectedServicePlanPricingPolicy.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteServicePlanPricingPolicyFormModal;
